import React, { useEffect, useRef } from 'react';
import { Toast, ToastBar, ToastPosition } from 'react-hot-toast';
import './ToastWithProgress.css';
import { computeRemainingFraction } from './utils/computeRemainingFraction';

interface ToastWithProgressProps {
  toast: Toast;
  pausedAt: number | undefined;
  position: ToastPosition;
}

const ToastWithProgress: React.FC<ToastWithProgressProps> = ({
  toast,
  pausedAt,
  position,
}) => {
  // Ref, not state: written every animation frame directly to the DOM,
  // so it never has to go through a React re-render.
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!toast.duration || toast.duration === Infinity) return undefined;

    let frameId: number | undefined;
    const tick = (): void => {
      const fraction = computeRemainingFraction(toast, pausedAt);
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${fraction})`;
      }
      if (!pausedAt) {
        frameId = requestAnimationFrame(tick);
      }
    };
    tick();

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
    };
  }, [toast.createdAt, toast.duration, toast.pauseDuration, pausedAt]);

  return (
    <ToastBar toast={toast} position={position}>
      {({ icon, message }) => (
        <>
          {icon}
          {message}
          <div ref={barRef} className="toast-progress" />
        </>
      )}
    </ToastBar>
  );
};

export default ToastWithProgress;
